import { Link } from '@lmig/lmds-react-link';
import { Heading } from '@lmig/lmds-react-typography';
import { useEffect, useState } from 'react';
import './InstantIncentivesTile.scss';
import { getISILink } from 'Foundation/Configurations/GlobalConfiguration';
import { useSessionStorage } from 'Utilities/UseStorage';

type ISICampaign = {
  name: string;
};
export interface IClearISISessionResponse {
  completed: boolean;
}

const InstantIncentivesTile = () => {
  const newOffersSessionStorageKey = 'NewISIOffers';
  const activatedOffersSessionStorageKey = 'ActivatedISIOffers';
  const expirationMinutes: number = 30;
  const [newOffers, setNewOffers] = useSessionStorage<ISICampaign[]>(
    newOffersSessionStorageKey,
    undefined,
    expirationMinutes,
  );
  const [activatedOffers, setActivatedOffers] = useSessionStorage<ISICampaign[]>(
    activatedOffersSessionStorageKey,
    undefined,
    expirationMinutes,
  );
  const [showMoreMessage, setShowMoreMessage] = useState<boolean>(false);

  useEffect(() => {
    const getISICampaigns = async () => {
      let data = await fetch('/api/sitecore/PortalUserAsync/GetInstantIncentivesCampaigns');
      if (data.status == 200) {
        let isi: { inititiatives: any[]; sessionDataExpiration: string } = await data.json();
        if (isi && isi.inititiatives) {
          let hasActivated = isi.inititiatives.find((x) => x.Agent.Status == 'Active');
          let hasNew = isi.inititiatives.find(
            (x) => x.Agent.Status == 'Participating' || x.Agent.Status == 'Registered',
          );

          let offerCount = 5;
          if (!hasNew || !hasActivated)
            //if we need to show 'no activated/no new offers message', show one less offer to make space for messaging
            offerCount -= 1;

          if (isi.inititiatives.length > offerCount) setShowMoreMessage(true);

          isi.inititiatives = isi.inititiatives
            .sort((a, b) => {
              if (a.Agent.Status == 'Active' && b.Agent.Status != 'Active') return 1;
              else if (a.Agent.Status == 'Active' && b.Agent.Status == 'Active')
                return new Date(a.Initiative.EndDate).getTime() < new Date(b.Initiative.EndDate).getTime() ? -1 : 1;
              //sort activated offers expiring soonest to top
              else if (a.Agent.Status != 'Active' && b.Agent.Status != 'Active')
                return new Date(a.Initiative.StartDate).getTime() > new Date(b.Initiative.StartDate).getTime() ? -1 : 1;
              //sort new offers starting earliest to top
              else return -1;
            })
            .slice(0, offerCount);

          setNewOffers({
            data: isi.inititiatives
              .filter((offer) => offer.Agent.Status == 'Participating' || offer.Agent.Status == 'Registered')
              .map((x) => ({ name: x.Initiative.AdditionalData.PortalTitle })),
          });
          setActivatedOffers({
            data: isi.inititiatives
              .filter((offer) => offer.Agent.Status == 'Active')
              .map((x) => ({ name: x.Initiative.AdditionalData.PortalTitle })),
          });
        }
      }
    };
    if (!newOffers && !activatedOffers) getISICampaigns();
  }, []);

  async function ClearSitecoreISISession() {
    try {
      const res = await fetch('/api/sitecore/PortalUserAsync/ClearISISessionCache', {
        method: 'POST',
      });
      if (res && res.headers) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.indexOf('application/json') !== -1) {
          const data: IClearISISessionResponse = await res.json();
          if (data?.completed !== null && data?.completed !== undefined) return data.completed;
        }
      }
    } catch (error: any) {
      console.error(error);
    }
    return false;
  }

  const clearISISessions = () => {
    ClearSitecoreISISession();
    sessionStorage.removeItem(newOffersSessionStorageKey);
    sessionStorage.removeItem(activatedOffersSessionStorageKey);
  };

  return (
    <div className="instant-incentives-tile tile">
      <div className="tile-header">
        <Heading type="h4-light">
          My Instant Incentives<span className="heading-trademark">TM</span>
        </Heading>
        <Link variant="standalone" href={getISILink()} target="_blank">
          See all
        </Link>
      </div>
      <div className="offer-heading new-offers">
        <Heading type="h6-medium">New offers</Heading>
      </div>
      {newOffers && newOffers.length > 0 ? (
        <div>
          <ul>
            {newOffers.map((x) => (
              <li>
                <div className="offer-item">
                  <p>{x.name}</p>
                  <Link
                    href={getISILink()}
                    target="_blank"
                    onClick={() => {
                      clearISISessions();
                    }}
                  >
                    Activate
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="no-offers-message">No new offers available.</div>
      )}
      {newOffers && newOffers.length < 5 && (
        <div className="offer-heading activated-offers">
          <Heading type="h6-medium">Activated</Heading>
        </div>
      )}
      {activatedOffers && activatedOffers.length > 0 ? (
        <div className="activated-offers">
          <ul>
            {activatedOffers.map((x) => (
              <li>
                <div className="offer-item">
                  <p>{x.name}</p>
                  <Link href={getISILink()} target="_blank">
                    Details
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="no-offers-message">No activated offers available.</div>
      )}
      {showMoreMessage && (
        <Link href={getISILink()} className="additional-offers" target="_blank" variant="standalone">
          View additional offers on Instant Incentives dashboard
        </Link>
      )}
      <div className="information-updated-text">Offer status may take up to 30 minutes to refresh.</div>
    </div>
  );
};

export default InstantIncentivesTile;
